import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderOpen, Plus, Loader2, Trash2 } from "lucide-react";
import { TrashDialog } from "../components/TrashDialog";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AppHeader } from "../components/AppHeader";
import { ProjectCard } from "../components/ProjectCard";
import { UsersPanel } from "../components/UsersPanel";
import { AlertsPanel } from "../components/AlertsPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const Grid = ({ list, nav, onDelete, owner, testId, empty }) => (
  list.length === 0 ? (
    <div data-testid={`${testId}-empty`} className="rounded-xl border border-dashed border-[#2D333F] p-12 text-center text-[#9CA3AF]">
      <FolderOpen className="w-10 h-10 mx-auto text-[#4B5563]" />
      <p className="mt-3">{empty}</p>
    </div>
  ) : (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={testId}>
      {list.map((p, i) => (
        <ProjectCard key={p.id} p={p} i={i} prefix={testId === "projects-list" ? "project" : testId} onOpen={() => nav(`/projeto/${p.id}`)} onDelete={() => onDelete(p)} owner={owner} />
      ))}
    </div>
  )
);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [trashOpen, setTrashOpen] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name: name.trim() || "Ambiente", client: client.trim() });
      nav(`/projeto/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setCreating(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/projects/${toDelete.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setToDelete(null);
      return;
    }
    toast.success("Ambiente enviado para a lixeira");
    setToDelete(null);
    load();
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="px-4 sm:px-6 lg:px-8 py-10 max-w-6xl mx-auto space-y-10">
        <section className="fade-up">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#E67E22]">Olá, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-2">Seus ambientes de corte</h1>
          <p className="text-[#9CA3AF] mt-2 max-w-xl">Crie um ambiente para o cliente, adicione as peças ou módulos prontos e gere o plano de corte otimizado para chapas de 2750 x 1850 mm.</p>
          <Button variant="outline" data-testid="open-trash-btn" onClick={() => setTrashOpen(true)} className="mt-4 border-[#2D333F] bg-transparent"><Trash2 className="w-4 h-4 mr-1" /> Lixeira</Button>
        </section>

        <form onSubmit={create} data-testid="new-project-form" className="rounded-xl border border-[#2D333F] bg-[#181B21] p-5 grid sm:grid-cols-[1fr_1fr_auto] gap-3 fade-up">
          <Input data-testid="new-project-client-input" value={client} onChange={(e) => setClient(e.target.value)} placeholder="Cliente" className="bg-[#0F1115] border-[#2D333F] h-11" />
          <Input data-testid="new-project-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ambiente" className="bg-[#0F1115] border-[#2D333F] h-11" />
          <Button data-testid="create-project-btn" disabled={creating} className="h-11 bg-[#E67E22] hover:bg-[#D35400] text-black font-semibold">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Novo ambiente
          </Button>
        </form>

        {user?.role === "admin" && <AlertsPanel />}
        {projects === null ? (
          <div className="flex items-center gap-2 text-[#9CA3AF]"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
        ) : user?.role === "admin" ? (
          <Tabs defaultValue="clientes">
            <TabsList className="bg-[#181B21] border border-[#2D333F] h-auto p-1 flex-wrap">
              <TabsTrigger value="clientes" data-testid="home-tab-clients" className="data-[state=active]:bg-[#E67E22] data-[state=active]:text-black px-4">Últimos ambientes dos clientes</TabsTrigger>
              <TabsTrigger value="meus" data-testid="home-tab-admin" className="data-[state=active]:bg-[#E67E22] data-[state=active]:text-black px-4">Projetos do administrador</TabsTrigger>
            </TabsList>
            <TabsContent value="clientes" className="mt-5 space-y-2">
              <Grid list={projects.filter((p) => p.user_id !== user.id).slice(0, 3)} nav={nav} onDelete={setToDelete} owner testId="projects-list" empty="Nenhum ambiente de cliente ainda." />
              <p className="text-xs text-[#6B7280]">Mostrando os 3 últimos. Para ver os outros, clique no cliente em “Usuários cadastrados”.</p>
            </TabsContent>
            <TabsContent value="meus" className="mt-5">
              <Grid list={projects.filter((p) => p.user_id === user.id)} nav={nav} onDelete={setToDelete} testId="admin-projects-list" empty="Nenhum projeto criado pelo administrador." />
            </TabsContent>
          </Tabs>
        ) : (
          <Grid list={projects} nav={nav} onDelete={setToDelete} testId="projects-list" empty="Nenhum ambiente ainda. Crie o primeiro acima." />
        )}
        {user?.role === "admin" && <UsersPanel />}
      </main>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#181B21] border-[#2D333F] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir ambiente?</AlertDialogTitle>
            <AlertDialogDescription>“{toDelete?.client ? `${toDelete.client} ` : ""}{toDelete?.name}” vai para a lixeira e pode ser restaurado em até 7 dias.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-project">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-project" onClick={remove} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <TrashDialog open={trashOpen} onClose={() => setTrashOpen(false)} onRestored={load} />
    </div>
  );
}
